import React, { useState, useEffect, ReactElement } from 'react';
import { Intl, useDebounce } from '@erecruit/core';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  BaseModalProps,
  H1,
  H3,
  Modal,
  Search,
  styled,
  Spinner,
  Container,
  CardListItem,
  Flex,
  InverseSelect,
  Text,
  Option,
} from '@erecruit/ui-kit';
import { Resources } from '@erecruit/types';

import {
  selectors,
  requestPositionTemplates,
  requestPositionTemplateDetails,
  resetPositionTemplate,
  fetchCompanies,
  setSelectedCompany,
  clearPositionTemplateDetails,
} from './state/reducer';
import { PositionTemplate } from '../AddNewPositionWizard/types';

const { FormattedMessage, injectIntl } = Intl;

interface RequestPositionProps extends BaseModalProps {
  intl: Intl.InjectedIntl;
  isLoading: boolean;
  companies: Resources.Company[];
  selectedCompany: number;
  positionTemplates: Resources.PositionTemplate[];
  populatedPositionTemplate?: PositionTemplate;
  requestPositionTemplates: typeof requestPositionTemplates;
  requestPositionTemplateDetails: typeof requestPositionTemplateDetails;
  resetPositionTemplate: typeof resetPositionTemplate;
  fetchCompanies: typeof fetchCompanies;
  setSelectedCompany: typeof setSelectedCompany;
  clearPositionTemplateDetails: typeof clearPositionTemplateDetails;
}

const StyledModal = styled(Modal)({
  '.ant-modal-body': {
    padding: 0,
    height: '80vh',
  },
});

const PositionedSearch = styled(Search)({
  '&': {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: -20,
  },
});

const BoxedFlex = styled(Flex)({
  width: 'fit-content',
  margin: '0 auto',
});

const Header = () => (
  <H1 align="center">
    <FormattedMessage
      id={messageIds.vms.newPositionWizard.requestPosition.title}
    />
    <H3 align="center" padding={[1, 0]} weight={400}>
      <FormattedMessage
        id={messageIds.vms.newPositionWizard.requestPosition.description}
      />
    </H3>
  </H1>
);

const emptyCompanySelectionId = -1;

const RequestPosition = ({
  intl,
  visible,
  isLoading,
  positionTemplates,
  requestPositionTemplates,
  requestPositionTemplateDetails,
  resetPositionTemplate,
  populatedPositionTemplate,
  companies,
  fetchCompanies,
  clearPositionTemplateDetails,
  selectedCompany,
  setSelectedCompany,
  ...baseModalProps
}: RequestPositionProps) => {
  const history = useHistory();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [reset, setReset] = useState(location?.state?.reset);
  const closeWizard = () => {
    clearPositionTemplateDetails();
    history.push('/positions');
  };

  if (reset && location.pathname !== '/requestPosition') {
    resetPositionTemplate();
    clearPositionTemplateDetails();
    history.push('/requestPosition');
  }

  const selectPositionTemplate = (
    positionTemplate?: Resources.PositionTemplate,
  ) => {
    setReset(false);
    if (positionTemplate) {
      requestPositionTemplateDetails(positionTemplate);
    }
  };

  const filterCompanies = (input: string, option: ReactElement) =>
    typeof option.props.children === 'string'
      ? option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      : false;

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    requestPositionTemplates({
      searchTerm: debouncedSearchTerm,
      companyId: selectedCompany,
    });
  }, [debouncedSearchTerm, requestPositionTemplates, selectedCompany]);

  useEffect(() => {
    if (populatedPositionTemplate && !reset) {
      history.push('wizard', {
        positionTemplate: populatedPositionTemplate,
        reset: false,
        referrer: '/positions',
      });
    }
  }, [history, populatedPositionTemplate, reset, resetPositionTemplate]);

  const onCompanyChange = (companyId: string) => {
    setSelectedCompany(Number(companyId));
  };

  return (
    <StyledModal
      {...baseModalProps}
      closable={true}
      onCancel={closeWizard}
      visible={visible !== undefined ? visible : true}
      footer={null}
      width={665}
    >
      <>
        <Container
          overflow="visible"
          position="relative"
          padding={8}
          divider="bottom"
        >
          <Header />
          {companies.length > 1 && (
            <BoxedFlex direction="column" padding={[2, 0, 6, 0]}>
              <Text padding={[1, 0]} color="bodySecondary">
                <FormattedMessage
                  id={messageIds.vms.newPositionWizard.requestPosition.company}
                />
              </Text>
              <InverseSelect
                width={290}
                value={selectedCompany}
                onChange={onCompanyChange}
                allowClear={false}
                showSearch
                filterOption={filterCompanies}
              >
                <Option
                  key={emptyCompanySelectionId}
                  value={emptyCompanySelectionId}
                >
                  <FormattedMessage
                    id={
                      messageIds.vms.newPositionWizard.requestPosition
                        .chooseCompany
                    }
                  />
                </Option>
                {companies.map(company => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </InverseSelect>
            </BoxedFlex>
          )}
          <PositionedSearch
            disabled={selectedCompany === emptyCompanySelectionId}
            inverse
            placeholder={intl.formatMessage({
              id:
                messageIds.vms.newPositionWizard.requestPosition
                  .searchPlaceholder,
            })}
            onChange={e => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
        </Container>
        <Flex direction="column" justify="center" padding={[12, 10]}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {positionTemplates.map(positionTemplate => (
                <CardListItem
                  key={positionTemplate.id}
                  onClick={() => selectPositionTemplate(positionTemplate)}
                >
                  <H3 weight={600} color="headerPrimary">
                    {positionTemplate.name} ({positionTemplate.id})
                  </H3>
                </CardListItem>
              ))}
              {positionTemplates.length === 0 && (
                <Text color="bodySecondary" align="center">
                  <FormattedMessage
                    id={
                      messageIds.vms.newPositionWizard.requestPosition
                        .noPositionTemplates
                    }
                  />
                </Text>
              )}
            </>
          )}
        </Flex>
      </>
    </StyledModal>
  );
};

export const RequestPositionWithIntl = injectIntl(RequestPosition);

export default connect(selectors.selectRequestPositionState, {
  requestPositionTemplates,
  requestPositionTemplateDetails,
  resetPositionTemplate,
  fetchCompanies,
  setSelectedCompany,
  clearPositionTemplateDetails,
})(RequestPositionWithIntl);
